import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, SmartphoneIcon, GlobeIcon, PhoneIcon, MailIcon, MessageSquareIcon, BrainCircuitIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { RequestsHero } from '../components/requests/RequestsHero';
import { ScatteredRequests } from '../components/requests/ScatteredRequests';
import { RequestOrchestration } from '../components/requests/RequestOrchestration';
import { RequestIntelligence } from '../components/requests/RequestIntelligence';
import { RichelieuProof } from '../components/requests/RichelieuProof';
import { RequestsFinalCTA } from '../components/requests/RequestsFinalCTA';
import { trackEvent as track } from '../utils/analytics';
// FR lexicon (locked) for the Requests / Requêtes BCITI page. EN untouched.
// Single map for diagram channels, the comparison columns, the intelligence
// diagram nodes and the industries matrix (headers + items). "ATNA",
// "Infrastructure" and "Action" are intentionally kept as-is.
const FR: Record<string, string> = {
  // Hero diagram — channels
  'Mobile App': 'Appli mobile',
  Website: 'Site web',
  Phone: 'Téléphone',
  Email: 'Courriel',
  'Social Media': 'Réseaux sociaux',
  // Hero diagram — bottom steps
  Prioritize: 'Prioriser',
  Assign: 'Assigner',
  Resolve: 'Résoudre',
  Analyze: 'Analyser',
  // Comparison — traditional column
  'Scattered Channels': 'Canaux dispersés',
  'Manual Triage': 'Tri manuel',
  'No Visibility': 'Aucune visibilité',
  'Lost Requests': 'Requêtes perdues',
  'Department Guesswork': 'Devinettes entre services',
  'Disconnected Reporting': 'Rapports déconnectés',
  // Comparison — BCITI column
  'Single Operational Intake': "Point d'entrée opérationnel unique",
  'SLA-Based Prioritization': 'Priorisation basée sur les SLA',
  'Complete Tracking': 'Suivi complet',
  'Closed-Loop Resolution': 'Résolution en boucle fermée',
  'Smart Assignment': 'Assignation intelligente',
  'Operational Analytics': 'Analytique opérationnelle',
  // Brossard proof attributes
  'Since 2017': 'Depuis 2017',
  'Live in Production': 'En production active',
  'Verified Identity Infrastructure': "Infrastructure d'identité vérifiée",
  'Closed-Loop Communications': 'Communications en boucle fermée',
  'Law 25 Ready': 'Conforme Loi 25',
  'Canadian Hosted Infrastructure': 'Infrastructure hébergée au Canada',
  // Intelligence diagram nodes
  Requests: 'Requêtes',
  'Operational Data': 'Données opérationnelles',
  'ATNA Insights': 'Analyses ATNA',
  'Verified Recommendations': 'Recommandations vérifiées',
  // Industries matrix — headers
  Cities: 'Villes',
  Universities: 'Universités',
  Airports: 'Aéroports',
  'Retail & Telecom': 'Commerce & Télécom',
  Healthcare: 'Santé',
  // Industries matrix — items
  Potholes: 'Nids-de-poule',
  Permits: 'Permis',
  '311 Requests': 'Requêtes 311',
  'Student Services': 'Services aux étudiants',
  'IT Support': 'Soutien TI',
  Facilities: 'Installations',
  'Campus Requests': 'Requêtes du campus',
  'Lost Property': 'Objets perdus',
  'Passenger Complaints': 'Plaintes des passagers',
  'Access Requests': "Demandes d'accès",
  Operations: 'Opérations',
  'Customer Support': 'Soutien à la clientèle',
  'Service Requests': 'Demandes de service',
  Claims: 'Réclamations',
  Escalations: 'Escalades',
  'Patient Requests': 'Demandes des patients',
  'Facility Issues': "Problèmes d'installation",
  'Service Coordination': 'Coordination des services',
  'Support Requests': 'Demandes de soutien'
};
// Workflow steps (title + description) for "From intake to resolution."
const STEP_FR: Record<string, {
  title: string;
  desc: string;
}> = {
  Intake: {
    title: 'Réception',
    desc: '(Appli, portail, téléphone, courriel, réseaux, ATNA)'
  },
  'Verified Identity': {
    title: 'Identité vérifiée',
    desc: 'Savoir qui fait la demande'
  },
  'SLA Prioritization': {
    title: 'Priorisation SLA',
    desc: 'Priorité selon vos règles'
  },
  'Smart Assignment': {
    title: 'Assignation intelligente',
    desc: 'Propulsé par ATNA. Achemine vers le bon service, la bonne équipe ou le bon opérateur.'
  },
  Resolution: {
    title: 'Résolution',
    desc: 'Collaboration interne et exécution des tâches'
  },
  'Closed Loop Communication': {
    title: 'Communication en boucle fermée',
    desc: 'Mises à jour automatiques au demandeur'
  },
  Analytics: {
    title: 'Analytique',
    desc: 'Intelligence opérationnelle'
  }
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  traditional: 'Scattered Channels',
  bciti: 'Single Operational Intake'
}, {
  traditional: 'Manual Triage',
  bciti: 'SLA-Based Prioritization'
}, {
  traditional: 'No Visibility',
  bciti: 'Complete Tracking'
}, {
  traditional: 'Lost Requests',
  bciti: 'Closed-Loop Resolution'
}, {
  traditional: 'Department Guesswork',
  bciti: 'Smart Assignment'
}, {
  traditional: 'Disconnected Reporting',
  bciti: 'Operational Analytics'
}];
const MATRIX_COLUMNS = [{
  title: 'Cities',
  items: ['Potholes', 'Permits', 'Infrastructure', '311 Requests']
}, {
  title: 'Universities',
  items: ['Student Services', 'IT Support', 'Facilities', 'Campus Requests']
}, {
  title: 'Airports',
  items: ['Lost Property', 'Passenger Complaints', 'Access Requests', 'Operations']
}, {
  title: 'Retail & Telecom',
  items: ['Customer Support', 'Service Requests', 'Claims', 'Escalations']
}, {
  title: 'Healthcare',
  items: ['Patient Requests', 'Facility Issues', 'Service Coordination', 'Support Requests']
}];
export function Requests() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  const stepTitle = (en: string) => lang === 'FR' ? STEP_FR[en]?.title ?? en : en;
  const stepDesc = (en: string, desc: string) => lang === 'FR' ? STEP_FR[en]?.desc ?? desc : desc;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Requests'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8654" data-mp="114ie8h635pwi">
      <Header data-id="element-8655" data-mp="wch53447sfap" />

      <main className="pt-[120px]" data-id="element-8656" data-mp="wbyz3frr2mpl">
        {/* HERO SECTION */}
        <RequestsHero data-id="element-8657" data-mp="wa4ek2ke4wm1" />

        {/* SECTION 2: THE OPERATIONAL PROBLEM */}
        <section className="py-20 lg:py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8658" data-mp="sadpc3aiiojm">
          <ScatteredRequests data-id="element-8659" data-mp="1otl2i7v86gzz" />
        </section>

        {/* SECTION 3: CONNECTED ORCHESTRATION */}
        <section className="py-28 lg:py-32 px-6 border-t border-[#E4E6EC]" data-id="element-8660" data-mp="1k0i8xzqg77js">
          <RequestOrchestration data-id="element-8661" data-mp="32pl04g1iwwmr" />
        </section>

        {/* SECTION 3B: OPERATIONAL INTELLIGENCE */}
        <section className="py-24 lg:py-28 px-6 border-t border-[#E4E6EC] bg-[#FBFBFE]" data-id="element-8662" data-mp="37oxnd1wb45pv">
          <RequestIntelligence data-id="element-8663" data-mp="3bp8499ln0gji" />
        </section>

        {/* SECTION 4: PROOF & RESULTS — VILLE DE RICHELIEU */}
        <section className="py-20 lg:py-24 px-6 border-t border-[#E4E6EC] bg-[#FBFBFE]" data-id="element-8664" data-mp="3gpdqga951au7">
          <RichelieuProof data-id="element-8665" data-mp="3lmmayhhrdqym" />
        </section>

        {/* FINAL CTA */}
        <section className="py-20 lg:py-24 px-6 border-t border-[#E4E6EC]" data-id="element-8666" data-mp="3qmigwma0nsgt">
          <RequestsFinalCTA data-id="element-8667" data-mp="3qkfef2rog6v7" />
        </section>
      </main>

      <Footer data-id="element-8668" data-mp="3verz1g4w3o8j" />
    </div>;
}